/**
 * SCOPE OWNED, AND WHAT WAS OBSERVED ON IT (`provider-outcome-2`).
 *
 * The construction convention this breaks is the **provider scorecard**: a
 * page that turns a firm's history into a number and quietly ranks people by
 * it. There is no number here, and there is nothing to sort by.
 *
 * What replaces it is narrower and true. Ownership windows say who held
 * which package over which days. Execution records that reach one of those
 * windows through a canonical linkage column are shown with their class,
 * their basis, and their evidence. Everything else says so.
 *
 * Three things this panel refuses to draw, each because the payload refuses
 * to support them:
 *
 *   NO ARROWS.   Records are listed by date. Nothing connects two of them,
 *                because nothing in the data connects two of them.
 *   NO DELTA.    Planned and actual manpower sit side by side and are never
 *                subtracted. The plan is this tenant's own.
 *   NO BLAME COLOUR. A material that arrived after a projected date is not a
 *                red badge on a firm — the projected date was the tenant's.
 *
 * Every heading is imported from `outcomePresentation`, where a test walks
 * it for verdict words. A heading typed straight into this file would slip
 * past that guard, so none are.
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import { ContractMismatchCallout } from "../runtime/RuntimePrimitives";
import { shortId } from "./exchangePresentation";
import {
  PROVIDER_OUTCOME_CONTRACT,
  OUTCOME_COPY,
  presentScopeOwnership,
  presentAttributedExecution,
  presentOutcomeEnvelope,
  presentOutcomeAvailability,
} from "./outcomePresentation";
import {
  Landmark,
  ScrollText,
  Scale,
  HelpCircle,
  Ban,
  Split,
  Users,
  Package,
  ShieldQuestion,
  ChevronDown,
  Lock,
} from "lucide-react";

/**
 * Fact-class tone. Note what is missing: no green and no red. UNKNOWN is not
 * a failing and CONFLICT is not an error — colouring either as one would say
 * something about the firm that no record here supports.
 */
const FACT_TONE = {
  derived: "border-sky-500/40 text-sky-300 bg-sky-500/10",
  asserted: "border-indigo-500/40 text-indigo-300 bg-indigo-500/10",
  unknown: "border-border text-muted-foreground bg-muted/40",
  conflict: "border-amber-500/40 text-amber-300 bg-amber-500/10",
};

const Ref = ({ reference }) => {
  if (!reference) return null;
  const type = reference.recordType || "record";
  const id = reference.recordId || reference.id;
  if (!id) return null;
  return (
    <span className="font-mono text-[10px] text-muted-foreground">
      {type} {shortId(id)}
    </span>
  );
};

const Caveat = ({ children, testId }) =>
  children ? (
    <p className="text-[10px] text-muted-foreground leading-relaxed" data-testid={testId}>
      {children}
    </p>
  ) : null;

/** A fact class always travels with its basis — one without the other is half a sentence. */
const FactBadges = ({ factClass, basis }) => (
  <span className="inline-flex flex-wrap items-center gap-1">
    <Badge variant="outline" className={`text-[10px] ${FACT_TONE[factClass.tone] || FACT_TONE.unknown}`}>
      {factClass.label}
    </Badge>
    <Badge variant="outline" className="text-[10px] border-border text-muted-foreground">
      {basis.label}
    </Badge>
  </span>
);

const DeliveryFact = ({ delivery }) => (
  <div className="space-y-1">
    <p className="text-xs">
      {delivery.relation}
      <span className="text-muted-foreground">
        {" "}
        — projected {delivery.expected || "not recorded"}, arrived {delivery.actual || "not recorded"}
      </span>
    </p>
    <Caveat>{delivery.projectionNote}</Caveat>
    <p className="text-[10px] text-muted-foreground">
      isProviderDelay: <span className="font-mono">{delivery.isProviderDelay}</span>
    </p>
  </div>
);

const FactRow = ({ fact }) => (
  <div className="rounded border border-border/60 px-2.5 py-2 space-y-1.5" data-testid="execution-fact">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <p className="text-xs font-medium">{fact.label}</p>
      <FactBadges factClass={fact.factClass} basis={fact.basis} />
    </div>
    {fact.isDelivery && fact.value ? (
      <DeliveryFact delivery={fact.value} />
    ) : (
      <p className="text-xs font-mono break-all">{String(fact.value)}</p>
    )}
    {fact.candidates ? (
      <div className="space-y-0.5" data-testid="fact-conflict">
        {fact.candidates.map((candidate, index) => (
          <p key={index} className="text-[11px] font-mono">
            {String(candidate.value ?? JSON.stringify(candidate))}
          </p>
        ))}
        <Caveat>Both answers stand. Nothing here picks between them.</Caveat>
      </div>
    ) : null}
    {fact.disputed ? (
      <Alert className="border-amber-500/40 bg-amber-500/10 py-2" data-testid="fact-disputed">
        <Split className="h-3.5 w-3.5" />
        <AlertDescription className="text-[10px] leading-relaxed">
          <span className="font-medium">{OUTCOME_COPY.disputedLabel}. </span>
          {fact.disputedNote}
        </AlertDescription>
      </Alert>
    ) : null}
    {fact.retroactive ? (
      <Caveat testId="fact-retroactive">
        {OUTCOME_COPY.retroactiveLabel} — {fact.retroactiveNote}
      </Caveat>
    ) : null}
    {/* Both notes, never one instead of the other: the attribution note is
        usually what explains why a fact attributes to nobody. */}
    {fact.note ? <Caveat>{fact.note}</Caveat> : null}
    {fact.attributionNote ? (
      <Caveat testId="fact-attribution-note">{fact.attributionNote}</Caveat>
    ) : null}
    <div className="flex flex-wrap gap-x-3 gap-y-0.5">
      {fact.sources.map((source, index) => (
        <Ref key={index} reference={source} />
      ))}
      {fact.alsoAssertedByHumanWindow.map((window, index) => (
        <span key={`h-${index}`} className="text-[10px] text-muted-foreground">
          also asserted by <Ref reference={window} />
        </span>
      ))}
    </div>
  </div>
);

const OwnershipWindow = ({ window }) => (
  <div className="rounded border border-border/60 px-2.5 py-2 space-y-1" data-testid="ownership-window">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="text-xs font-medium">
        {window.scopeRole || "Role not recorded"}
        <span className="text-muted-foreground"> on package {shortId(window.packageId)}</span>
      </span>
      <span className="inline-flex items-center gap-1">
        <Badge
          variant="outline"
          className={`text-[10px] ${
            window.open
              ? "border-indigo-500/40 text-indigo-300 bg-indigo-500/10"
              : "border-border text-muted-foreground"
          }`}
        >
          {window.open ? OUTCOME_COPY.openLabel : OUTCOME_COPY.endedLabel}
        </Badge>
        <Badge variant="outline" className="text-[10px] border-border text-muted-foreground">
          {window.basisLabel}
        </Badge>
      </span>
    </div>
    <p className="text-[11px] font-mono text-muted-foreground">{window.windowText}</p>
    <p className="text-[10px] text-muted-foreground">
      Authorized by {window.authorizedBy || "not recorded"}
      {window.authorizedAt ? ` on ${window.authorizedAt.slice(0, 10)}` : ""}
      {window.closedAt ? ` · ended ${window.closedAt.slice(0, 10)}` : ""}
      {window.endedReason ? ` (${window.endedReason})` : ""}
    </p>
    <Caveat testId="window-retroactive">{window.retroactiveCaveat}</Caveat>
    <Caveat testId="window-dangling">{window.danglingCaveat}</Caveat>
    {window.finerRefs.length ? (
      <>
        <p className="text-[10px] text-muted-foreground">
          {window.finerRefs.map((ref) => `${ref.kind} ${shortId(ref.id)}`).join(" · ")}
        </p>
        <Caveat>{window.finerRefsCaveat}</Caveat>
      </>
    ) : null}
  </div>
);

const PackageOwnership = ({ entry }) => (
  <div className="rounded border border-border/60 px-2.5 py-2 space-y-1" data-testid="ownership-package">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="text-xs font-mono">{shortId(entry.packageId)}</span>
      <Badge
        variant="outline"
        className={`text-[10px] ${
          entry.shape === "CONFLICT"
            ? "border-amber-500/40 text-amber-300 bg-amber-500/10"
            : "border-border text-muted-foreground"
        }`}
      >
        {entry.headline}
      </Badge>
    </div>
    {entry.detail ? <Caveat>{entry.detail}</Caveat> : null}
    {entry.holder ? (
      <p className="text-[11px]">
        <Ref reference={entry.holder} />
        {entry.attribution ? (
          <span className="text-muted-foreground"> · {entry.attribution}</span>
        ) : null}
      </p>
    ) : null}
    {entry.candidates.length ? (
      <div className="space-y-0.5">
        {entry.candidates.map((candidate, index) => (
          <p key={index} className="text-[11px]">
            <Ref reference={candidate.value} />
            <span className="text-muted-foreground"> from </span>
            <Ref reference={candidate.source} />
          </p>
        ))}
      </div>
    ) : null}
  </div>
);

const ManpowerDay = ({ day }) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" data-testid="manpower-day">
    <span className="font-mono">{day.logDate || "date not recorded"}</span>
    <span>
      planned <span className="font-mono">{day.plannedManpower ?? "not recorded"}</span>
    </span>
    <span>
      actual <span className="font-mono">{day.actualManpower ?? "not recorded"}</span>
    </span>
    <Badge
      variant="outline"
      className={`text-[10px] ${FACT_TONE[day.factClass.tone] || FACT_TONE.unknown}`}
    >
      {day.factClass.label}
    </Badge>
    {day.disputed ? (
      <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-300">
        {OUTCOME_COPY.disputedLabel}
      </Badge>
    ) : null}
    {day.percentCompleteToday ? (
      <span className="text-muted-foreground" data-testid="percent-today">
        percentCompleteToday {String(day.percentCompleteToday.value)} —{" "}
        {day.percentCompleteToday.factClass.label}
      </span>
    ) : null}
  </div>
);

const InstanceBlock = ({ instance }) => {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded border border-border" data-testid="execution-instance">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
          >
            <span className="min-w-0">
              <span className="text-xs font-medium block truncate">
                {instance.instanceName || `Work face ${shortId(instance.id)}`}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {instance.tieCount} tie{instance.tieCount === 1 ? "" : "s"} ·{" "}
                {instance.facts.length} fact{instance.facts.length === 1 ? "" : "s"} ·{" "}
                {instance.manpowerDayCount} manpower day
                {instance.manpowerDayCount === 1 ? "" : "s"} · {instance.refusedCount} declined
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5 shrink-0">
              {instance.weakTieOnly ? (
                <Badge variant="outline" className="text-[10px] border-border text-muted-foreground">
                  {OUTCOME_COPY.weakTieLabel}
                </Badge>
              ) : null}
              <ChevronDown
                className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
              />
            </span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-3 pb-3 space-y-3 border-t border-border pt-3">
            <section className="space-y-1">
              <p className="text-[11px] font-medium">{OUTCOME_COPY.tiesTitle}</p>
              {instance.ties.map((tie, index) => (
                <div key={index} className="text-[11px]" data-testid="execution-tie">
                  <span>{tie.linkage.label}</span>
                  <span className="text-muted-foreground"> · package {shortId(tie.packageId)} · </span>
                  <Ref reference={tie.source} />
                  {tie.note ? <Caveat>{tie.note}</Caveat> : null}
                </div>
              ))}
              <Caveat testId="weak-tie-caveat">{instance.weakTieCaveat}</Caveat>
              {instance.tiesTruncated ? (
                <Caveat testId="ties-truncated">
                  {instance.tieCount} ties in total. {OUTCOME_COPY.listCutShort}
                </Caveat>
              ) : null}
            </section>

            {instance.empty ? (
              <p className="text-[11px] text-muted-foreground" data-testid="instance-empty">
                {OUTCOME_COPY.instanceEmpty}
              </p>
            ) : null}

            {instance.facts.length ? (
              <section className="space-y-1.5">
                <p className="text-[11px] font-medium">{OUTCOME_COPY.factsTitle}</p>
                {instance.facts.map((fact, index) => (
                  <FactRow key={index} fact={fact} />
                ))}
              </section>
            ) : null}

            {instance.manpowerDays.length ? (
              <section className="space-y-1">
                <p className="text-[11px] font-medium flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" />
                  {OUTCOME_COPY.pulseTitle}
                </p>
                {instance.manpowerDays.map((day, index) => (
                  <ManpowerDay key={index} day={day} />
                ))}
                <Caveat testId="manpower-no-delta">{instance.manpowerNote}</Caveat>
                {instance.manpowerDaysTruncated ? (
                  <Caveat>
                    {instance.manpowerDayCount} days recorded; the listing above is cut short, so
                    these counts are floors.
                  </Caveat>
                ) : null}
              </section>
            ) : null}

            {instance.materials.length ? (
              <section className="space-y-1.5">
                <p className="text-[11px] font-medium flex items-center gap-1.5">
                  <Package className="w-3.5 h-3.5" />
                  {OUTCOME_COPY.materialsTitle}
                </p>
                {instance.materials.map((material) => (
                  <div key={material.id} className="space-y-1.5" data-testid="material-block">
                    <p className="text-[11px]">{material.materialName || shortId(material.id)}</p>
                    {material.facts.map((fact, index) => (
                      <FactRow key={index} fact={fact} />
                    ))}
                  </div>
                ))}
                {instance.materialsTruncated ? (
                  <Caveat testId="materials-truncated">{OUTCOME_COPY.listCutShort}</Caveat>
                ) : null}
              </section>
            ) : null}

            {instance.constraints.length ? (
              <section className="space-y-1">
                <p className="text-[11px] font-medium">{OUTCOME_COPY.constraintsTitle}</p>
                {instance.constraints.map((constraint) => (
                  <p key={constraint.id} className="text-[11px]" data-testid="execution-constraint">
                    <span className="font-mono">{constraint.constraintKey || "no key"}</span>
                    <span className="text-muted-foreground">
                      {" "}
                      · {constraint.cleared ? "cleared" : "observed"}
                      {constraint.observedAt ? ` ${constraint.observedAt.slice(0, 10)}` : ""}
                    </span>
                  </p>
                ))}
                <Caveat>{OUTCOME_COPY.constraintsNote}</Caveat>
                {instance.constraintsTruncated ? (
                  <Caveat testId="constraints-truncated">{OUTCOME_COPY.listCutShort}</Caveat>
                ) : null}
              </section>
            ) : null}

            {instance.refusedObservations.length ? (
              <section className="space-y-1">
                <p className="text-[11px] font-medium flex items-center gap-1.5">
                  <Scale className="w-3.5 h-3.5" />
                  {OUTCOME_COPY.refusedTitle}
                </p>
                <Caveat testId="refused-standing">{OUTCOME_COPY.refusedStanding}</Caveat>
                {instance.refusedObservations.map((refused) => (
                  <div key={refused.id} className="text-[11px]" data-testid="refused-observation">
                    <span className="font-mono">{refused.observationType || "observation"}</span>
                    {refused.observedAt ? (
                      <span className="text-muted-foreground"> {refused.observedAt.slice(0, 10)}</span>
                    ) : null}
                    {refused.projectionDetail ? (
                      <Caveat>{refused.projectionDetail}</Caveat>
                    ) : null}
                    <Caveat>{refused.note}</Caveat>
                  </div>
                ))}
                {instance.refusedObservationsTruncated ? (
                  <Caveat testId="refused-truncated">
                    {instance.refusedCount} in total. {OUTCOME_COPY.listCutShort}
                  </Caveat>
                ) : null}
              </section>
            ) : null}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

const ScopeOutcomesPanel = ({ payload, error, organizationName }) => {
  const availability = presentOutcomeAvailability(error);

  if (availability) {
    return (
      <Card className="border-border" data-testid="scope-outcomes">
        <CardContent className="py-3 px-4 space-y-1">
          <p className="text-xs text-muted-foreground">{availability.headline}</p>
          {availability.detail ? (
            <p className="text-[10px] text-muted-foreground">{availability.detail}</p>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  const envelope = presentOutcomeEnvelope(payload);
  if (!envelope) return null;
  const ownership = presentScopeOwnership(payload.scopeOwnership);
  const execution = presentAttributedExecution(payload.attributedExecution);

  return (
    <Card className="border-border" data-testid="scope-outcomes">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Landmark className="w-4 h-4 text-primary" />
          {OUTCOME_COPY.sectionTitle}
        </CardTitle>
        <p className="text-xs text-muted-foreground leading-relaxed">{OUTCOME_COPY.sectionLead}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <ContractMismatchCallout
          received={envelope.contractVersion}
          supported={PROVIDER_OUTCOME_CONTRACT}
        />

        {/* CAUSALITY — a stated constant with its own basis, not a placeholder. */}
        <Alert className="border-border bg-muted/30" data-testid="outcome-causality">
          <Ban className="h-4 w-4" />
          <AlertDescription className="space-y-1">
            <p className="text-[11px] font-medium">
              Causality: {envelope.causality}
              {envelope.causalityPermanent ? " — permanently" : ""}
            </p>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              {envelope.causalityStatement}
            </p>
            {envelope.causalityBasis.map((reason, index) => (
              <p key={index} className="text-[10px] text-muted-foreground leading-relaxed">
                · {reason}
              </p>
            ))}
          </AlertDescription>
        </Alert>

        {envelope.scoreWithheld ? (
          <Alert className="border-amber-500/40 bg-amber-500/10" data-testid="score-withheld">
            <ShieldQuestion className="h-4 w-4" />
            <AlertDescription className="text-[11px] leading-relaxed">
              {envelope.scoreWithheldNote}
            </AlertDescription>
          </Alert>
        ) : null}

        {/* THE CEILING — how far proof reaches, never a success badge. */}
        {envelope.ceiling ? (
          <div className="rounded border border-border px-3 py-2 space-y-1" data-testid="attribution-ceiling">
            <p className="text-[11px] font-medium">{OUTCOME_COPY.provenanceTitle}</p>
            <p className="text-[10px] text-muted-foreground leading-relaxed">{envelope.ceiling}</p>
          </div>
        ) : null}

        {/* ---------------- OWNERSHIP ---------------- */}
        {ownership ? (
          <section className="space-y-2" data-testid="ownership-section">
            <p className="text-xs font-medium flex items-center gap-1.5">
              <ScrollText className="w-3.5 h-3.5" />
              {OUTCOME_COPY.ownershipTitle}
            </p>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              {OUTCOME_COPY.ownershipLead}
            </p>
            {ownership.hasWindows ? (
              <div className="space-y-1.5">
                {ownership.windows.map((window) => (
                  <OwnershipWindow key={window.id} window={window} />
                ))}
                {ownership.windowsTruncated ? (
                  <Caveat testId="windows-truncated">
                    The window listing is cut short. Disagreements and gaps below are still derived
                    from the full set.
                  </Caveat>
                ) : null}
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground" data-testid="ownership-empty">
                {OUTCOME_COPY.ownershipEmpty}
              </p>
            )}

            {ownership.packages.length ? (
              <div className="space-y-1.5 pt-1">
                <p className="text-[11px] font-medium">{OUTCOME_COPY.packagesTitle}</p>
                {ownership.packages.map((entry) => (
                  <PackageOwnership key={entry.packageId} entry={entry} />
                ))}
              </div>
            ) : null}

            {ownership.conflicts.length ? (
              <div className="space-y-1.5 pt-1">
                <p className="text-[11px] font-medium flex items-center gap-1.5">
                  <Split className="w-3.5 h-3.5" />
                  {OUTCOME_COPY.disputesTitle}
                </p>
                <p className="text-[10px] text-muted-foreground">{OUTCOME_COPY.disputesLead}</p>
                {ownership.conflicts.map((conflict, index) => (
                  <div
                    key={index}
                    className="rounded border border-amber-500/30 bg-amber-500/5 px-2.5 py-2 space-y-1"
                    data-testid="ownership-conflict"
                  >
                    <p className="text-[11px] font-medium">{conflict.display.label}</p>
                    <p className="text-[10px] text-muted-foreground leading-relaxed">
                      {conflict.detail || conflict.display.meaning}
                    </p>
                    {conflict.bidPackageId ? (
                      <p className="text-[10px] font-mono text-muted-foreground">
                        package {shortId(conflict.bidPackageId)}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}

            {ownership.unknowns.length ? (
              <div className="space-y-1.5 pt-1">
                <p className="text-[11px] font-medium flex items-center gap-1.5">
                  <HelpCircle className="w-3.5 h-3.5" />
                  {OUTCOME_COPY.gapsTitle}
                </p>
                {ownership.unknowns.map((unknown, index) => (
                  <div key={index} className="space-y-0.5" data-testid="ownership-gap">
                    <p className="text-[11px]">{unknown.display.label}</p>
                    <p className="text-[10px] text-muted-foreground leading-relaxed">
                      {unknown.detail || unknown.display.meaning}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}

            {ownership.attributionGrounds.length ? (
              <div className="pt-1 space-y-0.5" data-testid="attribution-grounds">
                {ownership.attributionGrounds.map((ground) => (
                  <p key={ground.basis} className="text-[10px] text-muted-foreground">
                    {ground.label} → <span className="font-mono">{ground.strength}</span>
                  </p>
                ))}
              </div>
            ) : null}
          </section>
        ) : null}

        {/* ---------------- OBSERVED EXECUTION ---------------- */}
        {execution ? (
          <section className="space-y-2" data-testid="execution-section">
            <p className="text-xs font-medium flex items-center gap-1.5">
              <Landmark className="w-3.5 h-3.5" />
              {OUTCOME_COPY.executionTitle}
            </p>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              {OUTCOME_COPY.executionLead}
            </p>

            {execution.instances.length ? (
              <>
                <div className="flex flex-wrap gap-1.5" data-testid="execution-tally">
                  {["HUMAN_ASSERTED", "DERIVED_FACT", "UNKNOWN", "CONFLICT"].map((key) => (
                    <Badge
                      key={key}
                      variant="outline"
                      className="text-[10px] border-border text-muted-foreground"
                    >
                      {key}: {execution.tally[key]}
                    </Badge>
                  ))}
                  {execution.tally.attributedToNobody ? (
                    <Badge
                      variant="outline"
                      className="text-[10px] border-amber-500/40 text-amber-300"
                    >
                      {OUTCOME_COPY.disputedLabel}: {execution.tally.attributedToNobody}
                    </Badge>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  {execution.instances.map((instance) => (
                    <InstanceBlock key={instance.id} instance={instance} />
                  ))}
                </div>
                {execution.instancesTruncated ? (
                  <Caveat>
                    {execution.instanceCount} tied work faces; the listing above is cut short.
                  </Caveat>
                ) : null}
              </>
            ) : (
              <p className="text-[11px] text-muted-foreground" data-testid="execution-empty">
                {execution.note || OUTCOME_COPY.executionEmpty}
              </p>
            )}

            <Caveat testId="dispute-scan-truncated">{execution.disputeScanNote}</Caveat>

            {execution.disputedPackages.length ? (
              <div className="space-y-0.5" data-testid="disputed-packages">
                <p className="text-[11px] font-medium">{OUTCOME_COPY.disputesTitle}</p>
                {execution.disputedPackages.map((reference, index) => (
                  <p key={index} className="text-[10px]">
                    <Ref reference={reference} />
                  </p>
                ))}
              </div>
            ) : null}

            {execution.neverEmitted.length ? (
              <div className="space-y-0.5 pt-1" data-testid="outcome-never-emitted">
                <p className="text-[11px] font-medium">{OUTCOME_COPY.neverEmittedTitle}</p>
                {execution.neverEmitted.map((entry, index) => (
                  <p key={index} className="text-[10px] text-muted-foreground">
                    · {entry}
                  </p>
                ))}
              </div>
            ) : null}
          </section>
        ) : null}

        {/* ---------------- WHAT THIS READ COULD NOT ESTABLISH ---------------- */}
        {envelope.missingInformation.length ? (
          <section className="space-y-0.5" data-testid="outcome-missing">
            <p className="text-[11px] font-medium">{OUTCOME_COPY.missingTitle}</p>
            {envelope.missingInformation.map((sentence, index) => (
              <p key={index} className="text-[10px] text-muted-foreground leading-relaxed">
                · {sentence}
              </p>
            ))}
          </section>
        ) : null}

        {envelope.scanTruncated.length ? (
          <section className="space-y-0.5" data-testid="outcome-truncated">
            <p className="text-[11px] font-medium">{OUTCOME_COPY.truncatedTitle}</p>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              {OUTCOME_COPY.truncatedLead}
            </p>
            <p className="text-[10px] font-mono text-muted-foreground">
              {envelope.scanTruncated.join(" · ")}
            </p>
          </section>
        ) : null}

        {envelope.privacy ? (
          <p className="text-[10px] text-muted-foreground flex items-start gap-1.5" data-testid="outcome-privacy">
            <Lock className="w-3 h-3 mt-0.5 shrink-0" />
            <span className="leading-relaxed">
              {envelope.privacy}
              {organizationName ? ` (${organizationName})` : ""}
            </span>
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default ScopeOutcomesPanel;
